import React from 'react';

/**
 * Pace Tag System
 *
 * Coaches write workout text using shorthand tags like [EASY] or [TEMPO].
 * When athletes view their plan, tags are replaced with their actual paces
 * derived from their VDOT benchmark calculation.
 *
 * When a tag follows a rep×distance pattern (e.g. "4x200 [SPEED]"), the
 * resolved text also includes the split time for that distance:
 *   4x200 [SPEED]  →  4x200 (30s · 4:00/mi)
 *   4x600 [SPEED]  →  4x600 (1:26 · 4:00/mi)
 *   4xMile [TEMPO] →  4xMile (5:52 · 5:52/mi)
 */

const METERS_PER_MILE = 1609.344;

// All supported pace tags mapped to their VDOT zone key
export const PACE_TAGS = [
  { tag: '[RECOVERY]', zone: 'recovery',           label: 'Recovery' },
  { tag: '[EASY]',     zone: 'easy',               label: 'Easy' },
  { tag: '[MODERATE]', zone: 'moderate',           label: 'Moderate' },
  { tag: '[SUBTEMPO]', zone: 'sub_tempo',          label: 'Sub-Tempo' },
  { tag: '[TEMPO]',     zone: 'tempo',              label: 'Tempo' },
  { tag: '[LT2]',      zone: 'lt2',               label: 'LT2' },
  { tag: '[LT1]',      zone: 'lt1',               label: 'LT1' },
  { tag: '[APOWER]',   zone: 'aerobic_power',      label: 'Aerobic Power' },
  { tag: '[VO2MAX]',   zone: 'vo2max',             label: 'VO2 Max' },
  { tag: '[MIXED]',    zone: 'mixed_zone',         label: 'Mixed Zone' },
  { tag: '[ANCAP]',    zone: 'anaerobic_capacity', label: 'An. Capacity' },
  { tag: '[ANPOWER]',  zone: 'anaerobic_power',    label: 'An. Power' },
  { tag: '[SE]',       zone: 'speed_endurance',    label: 'Speed End.' },
  { tag: '[SPEED]',    zone: 'speed',              label: 'Speed' },
];

// tag inner key (e.g. 'SPEED') → zone key (e.g. 'repetition')
const TAG_TO_ZONE = Object.fromEntries(
  PACE_TAGS.map(({ tag, zone }) => [tag.slice(1, -1), zone])
);

function distToMeters(dist, unit) {
  const d = dist ? parseFloat(dist) : 1;
  if (!unit) return d;
  const u = unit.toLowerCase();
  if (u === 'mi' || u.startsWith('mile')) return d * METERS_PER_MILE;
  if (u === 'km' || u === 'k') return d * 1000;
  return d; // meters
}

function paceStrToSec(paceStr) {
  if (!paceStr) return null;
  const parts = paceStr.split(':').map(Number);
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return null;
}

/** ≤500m → seconds ("32.4s"), >500m → M:SS ("1:26") */
function formatSplit(seconds, distMeters) {
  if (distMeters <= 500) {
    const rounded = Math.round(seconds * 10) / 10;
    return `${rounded % 1 === 0 ? rounded : rounded.toFixed(1)}s`;
  }
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Replace pace tags in workout text with the athlete's actual paces.
 * When a tag follows a rep×distance pattern, the split time is included.
 * @param {string} text — raw workout text containing tags
 * @param {object|null} paces — output of calculatePaces(), or null if no benchmark
 * @returns {string} resolved text
 */
export function resolvePaceTags(text, paces) {
  if (!text) return '';
  if (!paces) return text;

  // Helpers for timed interval resolution
  function timedSec(num, unit) {
    const u = (unit || '').trim().toLowerCase();
    const n = parseFloat(num);
    return (u === '"' || u === '\u201d' || u === 's' || u.startsWith('sec')) ? n : n * 60;
  }
  function fmtDur(num, unit) {
    const u = unit.trim();
    return u.length <= 1 ? `${num}${u}` : `${num} ${u}`;
  }
  const _UNIT = `['"'\u2019"\u201d]|min(?:utes?)?\\b|sec(?:onds?)?\\b|s\\b`;

  // Pass GC: "1600m GC (400/800/400) [SPEED], [LT2], [SPEED]"
  // Resolves each sub-segment to its split time, plus a total. Tags are consumed here and
  // NOT passed through to later passes.
  let resolved = text.replace(
    /(\d+(?:\.\d+)?)\s*(m(?:eters?)?|km|k|miles?|mi)?\s*GC\s*\(([^)]+)\)((?:\s*,?\s*\[[A-Z][A-Z0-9]*\])+)/gi,
    (match, totalDist, totalUnit, partsStr, tagsStr) => {
      const parts = partsStr.split('/').map(p => p.trim());
      const tags = [...tagsStr.matchAll(/\[([A-Z][A-Z0-9]*)\]/gi)].map(m => m[1]);
      if (parts.length !== tags.length || parts.length === 0) return match;
      const splitData = [];
      for (let i = 0; i < parts.length; i++) {
        const dm = parts[i].match(/^(\d+(?:\.\d+)?)\s*(m(?:eters?)?|km|k|miles?|mi)?$/i);
        if (!dm) return match;
        const distMeters = distToMeters(dm[1], dm[2]);
        const zone = TAG_TO_ZONE[tags[i].toUpperCase()];
        if (!zone || !paces[zone]?.perMile) return match;
        const paceSec = paceStrToSec(paces[zone].perMile);
        if (!paceSec) return match;
        const splitSec = (distMeters / METERS_PER_MILE) * paceSec;
        splitData.push({ distMeters, splitSec });
      }
      const totalSec = splitData.reduce((sum, s) => sum + s.splitSec, 0);
      const splitStrs = splitData.map(s => formatSplit(s.splitSec, s.distMeters));
      const tm = Math.floor(totalSec / 60);
      const ts = Math.round(totalSec % 60);
      const totalStr = `${tm}:${String(ts).padStart(2, '0')}`;
      const outerLabel = `${totalDist}${totalUnit || 'm'}`;
      return `${outerLabel} GC (${splitStrs.join(', ')}\u00a0\u00b7\u00a0${totalStr} total)`;
    }
  );

  // Pass 0A: tag after duration — "8x3'[LT1] on 1'[EASY] off"
  resolved = resolved.replace(
    new RegExp(
      `(\\d+(?:\\.\\d+)?)\\s*[xX×]\\s*(\\d+(?:\\.\\d+)?)\\s*(${_UNIT})\\s*\\[([A-Z][A-Z0-9]*)\\](\\s*on\\s*(\\d+(?:\\.\\d+)?)\\s*(${_UNIT})\\s*\\[([A-Z][A-Z0-9]*)\\](\\s*off)?)?`,
      'gi'
    ),
    (match, count, wDur, wUnit, wKey, _rPart, rDur, rUnit, rKey) => {
      const wZone = TAG_TO_ZONE[wKey.toUpperCase()];
      if (!wZone || !paces[wZone]) return match;
      const wPaceSec = paceStrToSec(paces[wZone].perMile);
      if (!wPaceSec) return match;
      const wMiles = (timedSec(wDur, wUnit) / wPaceSec).toFixed(2);
      let out = `${count}x${fmtDur(wDur, wUnit)} (${wMiles}\u00a0mi\u00a0·\u00a0${paces[wZone].perMile}/mi)`;
      if (rDur && rKey) {
        const rZone = TAG_TO_ZONE[rKey.toUpperCase()];
        if (rZone && paces[rZone]) {
          const rPaceSec = paceStrToSec(paces[rZone].perMile);
          if (rPaceSec) {
            const rMiles = (timedSec(rDur, rUnit) / rPaceSec).toFixed(2);
            out += ` on ${fmtDur(rDur, rUnit)} (${rMiles}\u00a0mi\u00a0·\u00a0${paces[rZone].perMile}/mi)`;
          }
        }
      }
      return out;
    }
  );

  // Pass 0B: tag after on/off keyword — "4x30" on [LT1] 30" off [RECOVERY]"
  resolved = resolved.replace(
    new RegExp(
      `(\\d+(?:\\.\\d+)?)\\s*[xX×]\\s*(\\d+(?:\\.\\d+)?)\\s*(${_UNIT})\\s*on\\s*\\[([A-Z][A-Z0-9]*)\\]\\s*(\\d+(?:\\.\\d+)?)\\s*(${_UNIT})\\s*off\\s*\\[([A-Z][A-Z0-9]*)\\]`,
      'gi'
    ),
    (match, count, wDur, wUnit, wKey, rDur, rUnit, rKey) => {
      const wZone = TAG_TO_ZONE[wKey.toUpperCase()];
      if (!wZone || !paces[wZone]) return match;
      const wPaceSec = paceStrToSec(paces[wZone].perMile);
      if (!wPaceSec) return match;
      const wMiles = (timedSec(wDur, wUnit) / wPaceSec).toFixed(2);
      let out = `${count}x${fmtDur(wDur, wUnit)} on (${wMiles}\u00a0mi\u00a0·\u00a0${paces[wZone].perMile}/mi)`;
      const rZone = TAG_TO_ZONE[rKey.toUpperCase()];
      if (rZone && paces[rZone]) {
        const rPaceSec = paceStrToSec(paces[rZone].perMile);
        if (rPaceSec) {
          const rMiles = (timedSec(rDur, rUnit) / rPaceSec).toFixed(2);
          out += ` ${fmtDur(rDur, rUnit)} off (${rMiles}\u00a0mi\u00a0·\u00a0${paces[rZone].perMile}/mi)`;
        }
      }
      return out;
    }
  );

  // Pass 0C: NxDur<unit> [desc words] [TAG] — "6x30" hills [APOWER]" → "6x30" hills (0.11 mi · pace/mi)"
  // Handles timed reps with descriptive text between unit and tag; negative lookahead prevents matching on/off patterns
  resolved = resolved.replace(
    new RegExp(
      `(\\d+(?:\\.\\d+)?)\\s*[xX×]\\s*(\\d+(?:\\.\\d+)?)\\s*(${_UNIT})\\s*(?!\\bon\\b|\\boff\\b)([^[\\n]*?)\\s*\\[([A-Z][A-Z0-9]*)\\]`,
      'gi'
    ),
    (match, count, wDur, wUnit, desc, wKey) => {
      const wZone = TAG_TO_ZONE[wKey.toUpperCase()];
      if (!wZone || !paces[wZone]) return match;
      const wPaceSec = paceStrToSec(paces[wZone].perMile);
      if (!wPaceSec) return match;
      const wMiles = (timedSec(wDur, wUnit) / wPaceSec).toFixed(2);
      const label = desc.trim() ? `${desc.trim()} ` : '';
      return `${count}x${fmtDur(wDur, wUnit)} ${label}(${wMiles}\u00a0mi\u00a0·\u00a0${paces[wZone].perMile}/mi)`;
    }
  );

  // Pass 0b: single timed segments — "1'[ANPOWER]" → "1' (0.XX mi · pace/mi)"
  resolved = resolved.replace(
    /(\d+(?:\.\d+)?)['\u2019]\s*\[([A-Z][A-Z0-9]*)\]/gi,
    (match, min, tagKey) => {
      const zone = TAG_TO_ZONE[tagKey.toUpperCase()];
      if (!zone || !paces[zone]) return match;
      const paceSec = paceStrToSec(paces[zone].perMile);
      if (!paceSec) return match;
      const miles = ((parseFloat(min) * 60) / paceSec).toFixed(2);
      return `${min}' (${miles}\u00a0mi\u00a0·\u00a0${paces[zone].perMile}/mi)`;
    }
  );

  // Pass 1: context-aware — NxD[unit] [TAG] → (split) or (split · pace/mi)
  // Matches: 4x200 [SPEED], 4x200m at [SPEED], 4xMile @ [TEMPO], 4x3K [INTERVAL], etc.
  // For ≤800m, shows just the split; for longer, includes pace/mi.
  resolved = resolved.replace(
    /(\d+(?:\.\d+)?)\s*[xX×]\s*(\d+(?:\.\d+)?)?\s*(miles?|mi|km|k|meters?|m)?\s*(?:at\s+|@\s*|pace\s+)?\[([A-Z][A-Z0-9]*)\]/gi,
    (match, _count, dist, unit, tagKey) => {
      const zone = TAG_TO_ZONE[tagKey.toUpperCase()];
      if (!zone || !paces[zone]) return match;
      const paceSec = paceStrToSec(paces[zone].perMile);
      if (!paceSec) return match;
      if (!dist && !unit) return match.replace(`[${tagKey}]`, `${paces[zone].perMile}/mi`);
      const meters = distToMeters(dist, unit);
      const splitSec = (meters / METERS_PER_MILE) * paceSec;
      const splitStr = formatSplit(splitSec, meters);
      if (meters <= 800) return match.replace(`[${tagKey}]`, `(${splitStr})`);
      return match.replace(`[${tagKey}]`, `(${splitStr}\u00a0·\u00a0${paces[zone].perMile}/mi)`);
    }
  );

  // Pass 1b: standalone short distance + tag (≤800m) → split only, no pace/mi
  // Matches: 500m [VO2MAX], 200 [SPEED], 400m @ [ANCAP]
  resolved = resolved.replace(
    /(\d+(?:\.\d+)?)\s*(meters?|m)?\s*(?:at\s+|@\s*|pace\s+)?\[([A-Z][A-Z0-9]*)\]/gi,
    (match, dist, unit, tagKey) => {
      const zone = TAG_TO_ZONE[tagKey.toUpperCase()];
      if (!zone || !paces[zone]) return match;
      const paceSec = paceStrToSec(paces[zone].perMile);
      if (!paceSec) return match;
      const meters = distToMeters(dist, unit || 'm');
      if (meters > 800) return match;
      const splitSec = (meters / METERS_PER_MILE) * paceSec;
      const splitStr = formatSplit(splitSec, meters);
      return match.replace(`[${tagKey}]`, `(${splitStr})`);
    }
  );

  // Pass 1c: progression end tag — "NxD (split) > [TAG]" → resolve using same distance
  resolved = resolved.replace(
    /(\d+(?:\.\d+)?)\s*[xX×]\s*(\d+(?:\.\d+)?)?\s*(miles?|mi|km|k|meters?|m)?\s*\([^)]+\)\s*>\s*\[([A-Z][A-Z0-9]*)\]/gi,
    (match, _count, dist, unit, tagKey) => {
      const zone = TAG_TO_ZONE[tagKey.toUpperCase()];
      if (!zone || !paces[zone]) return match;
      const paceSec = paceStrToSec(paces[zone].perMile);
      if (!paceSec) return match;
      const meters = distToMeters(dist, unit);
      const splitSec = (meters / METERS_PER_MILE) * paceSec;
      const splitStr = formatSplit(splitSec, meters);
      if (meters <= 800) return match.replace(`[${tagKey}]`, `(${splitStr})`);
      return match.replace(`[${tagKey}]`, `(${splitStr}\u00a0·\u00a0${paces[zone].perMile}/mi)`);
    }
  );

  // Pass 2: any remaining standalone tags → just pace/mi
  for (const { tag, zone } of PACE_TAGS) {
    if (paces[zone]) {
      resolved = resolved.replaceAll(tag, `${paces[zone].perMile}/mi`);
    }
  }

  // Pass 3: cycle rest annotation on resolved text.
  // After Pass 1 produces "(34s · 5:00/mi)", look for a following cycle marker
  // and compute the expected rest: "(34s · 26s rest · 5:00/mi)"
  // Handles second-based: N" cycle  |  N s cycle  |  N sec cycle
  //         minute-based: N' cycle  |  N min cycle
  function cycleToSec(cycleN, unit) {
    const n = parseFloat(cycleN);
    const u = unit.trim().toLowerCase();
    return (u === "'" || u === '\u2019' || u.startsWith('min')) ? Math.round(n * 60) : Math.round(n);
  }
  function splitToSec(split) {
    if (split.includes(':')) {
      const [m, s] = split.split(':').map(Number);
      return m * 60 + s;
    }
    return parseFloat(split); // "34s" → 34, "32.4s" → 32.4
  }
  function restStr(sec) {
    const rounded = Math.round(sec * 10) / 10;
    if (rounded < 60) return `${rounded % 1 === 0 ? rounded : rounded.toFixed(1)}s`;
    return `${Math.floor(rounded / 60)}:${String(Math.round(rounded % 60)).padStart(2, '0')}`;
  }
  resolved = resolved.replace(
    /\((\d+(?:\.\d+)?s|\d+:\d{2})\u00a0·\u00a0([\d:]+\/mi)\)\s*(\d+(?:\.\d+)?)\s*(["\u201d''\u2019]|s(?:ec(?:onds?)?)?\b|min(?:utes?)?\b)\s*cycle/gi,
    (match, split, pace, cycleN, cycleUnit) => {
      const splitSec = splitToSec(split);
      const cycleSec = cycleToSec(cycleN, cycleUnit);
      const rest = Math.round((cycleSec - splitSec) * 10) / 10;
      if (rest <= 0) return match;
      return `(${split}\u00a0·\u00a0${pace})\u00a0·\u00a0${restStr(rest)}\u00a0rest`;
    }
  );

  // Pass 4: "equal rest" — replace with the split time from the resolved annotation.
  // After Pass 1: "6x200 (32s · 4:00/mi) w/ equal rest" → "6x200 (32s · 4:00/mi) w/ 32s rest"
  resolved = resolved.replace(
    /\((\d+(?:\.\d+)?s|\d+:\d{2})\u00a0·\u00a0([\d:]+\/mi)\)\s*(?:w\/\s*|with\s+)?equal\s+rest/gi,
    (match, split, pace) => {
      return `(${split}\u00a0·\u00a0${pace})\u00a0·\u00a0${split}\u00a0rest`;
    }
  );

  return resolved;
}

/**
 * Check whether a text string contains any pace tags.
 * @param {string} text
 * @returns {boolean}
 */
export function hasPaceTags(text) {
  if (!text) return false;
  return PACE_TAGS.some(({ tag }) => text.includes(tag));
}

/**
 * Insert a tag at the cursor position in a textarea.
 * Call this from an onClick handler for tag buttons.
 * @param {HTMLTextAreaElement} textarea
 * @param {string} tag — e.g. '[EASY]'
 * @param {function} setValue — React state setter for the textarea value
 */
export function insertTagAtCursor(textarea, tag, setValue) {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const current = textarea.value;
  const newValue = current.slice(0, start) + tag + current.slice(end);
  setValue(newValue);

  // Restore cursor position after React re-renders
  requestAnimationFrame(() => {
    textarea.selectionStart = start + tag.length;
    textarea.selectionEnd = start + tag.length;
    textarea.focus();
  });
}

/**
 * Render workout text with *...* description blocks displayed as italics.
 * Returns JSX (array of strings and <em> elements).
 */
export function renderWorkoutText(text) {
  if (!text) return '';
  const parts = text.split(/\*([^*]+)\*/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    i % 2 === 1 ? <em key={i}>{part}</em> : part
  );
}
